/**
 * The coach chrome both clients show: the one home for it. Today that is the
 * controls around the coach's opening line -- #269's "Got it", and, when the
 * training verdict speaks (#270), the "Why" readout, its history disclosure,
 * and the proposal's controls and record lines, and, when a line offers to
 * add an exercise to a template (PC-I75), the pill, the declines, the scope
 * line and the record lines every such offer shares. Chrome only one client
 * shows (iOS Home's x label, the comeback question sheet) stays with its
 * client.
 *
 * A THIRD deck, deliberately, rather than more entries in `firstRun.ts` or
 * `catalog.ts`. Both scope themselves by their own docstrings, and this copy
 * is neither first run nor catalog setup. Widening a guard's remit past what
 * the people who set that remit agreed to is how a guard stops meaning
 * anything.
 *
 * Its iOS twin is `ios/GroundworkKit/Sources/GroundworkKit/CoachCopy.swift`
 * (`enum CoachCopy`), and `python3 scripts/check_copy_decks.py` compares the
 * two on every symbol they share. **A string added here belongs there too.**
 * The web coach page and the phone say the same words about the same verdict
 * only if something mechanical checks it.
 *
 * Interface chrome, so English whatever the coach speaks. The coach's own
 * line above these controls is the backend's, in the coach language, and is
 * not deck text. The wordings are orchestrator rulings under the owner's
 * delegation: PC-I40 to PC-I44, PC-I52, PC-I54, PC-I55 and PC-I61.
 * Placeholders are `{name}`-style and substituted with `fill()` from
 * `./firstRun`. No em dashes (PRODUCT.md:47).
 */
export const COACH_COPY = {
  // ── The line's controls (PC-I7, PC-I41) ──
  openerGotIt: 'Got it',
  // Under the controls when an answer or a dismissal was not saved and
  // trying again can work: every write the opener makes, #269's "Got it"
  // and Home's x included (PC-I54). The controls stay as they were. A write
  // refused because the line is out of date reloads it instead (PC-I61).
  openerWriteFailed: "Couldn't save that. Try again.",

  // ── One tap away (PC-I40, PC-I43, PC-I44) ──
  verdictWhy: 'Why',
  // The web's alone: the readout expands in the bubble and folds with this.
  // iOS's "Why" opens a sheet, which the phone closes itself, so the iOS
  // deck has no twin, and chrome only one client shows stays with it.
  verdictHide: 'Hide',
  // The readout's eyebrow names the verdict. A verdict this client does not
  // know reads as today's verdict, never as "train" and never as a guessed
  // band (#253, PC-I44).
  verdictEyebrowEase: 'Ease off',
  verdictEyebrowDeload: 'Lighter week',
  verdictEyebrowRest: 'Rest',
  verdictEyebrowUnknown: "Today's verdict",
  // #245's disclosure, always shown, with real counts only (PC-I43). The
  // short wording is the web Standing ledger's own sentence.
  verdictHistoryLabel: 'History',
  verdictHistoryShort:
    'Recovery is reading {have} of the {need} days it needs. Sleep and HRV sit out until then.',
  verdictHistoryNone: 'No health history yet. Sleep and HRV sit out until it arrives.',
  verdictHistoryEnough: 'Built on {have} days of health history.',

  // ── The lighter session, a proposal (PC-I41, PC-I42) ──
  proposalEase: 'Ease my next session',
  proposalGoLighter: "I'll go lighter",
  proposalKeepPlan: 'Keep my plan',
  proposalScope: 'Your next session only. Your plan stays as it is.',
  proposalEasedRecord: 'Your next session is eased. Each set shows why.',
  proposalLighterRecord: 'Noted: a lighter day.',
  proposalUndo: 'Undo',

  // ── The injured area's week-later ask (PC-I68, PC-I138) ──
  // "It's clear" and "Not yet" have equal weight, so neither is nudged. The
  // record lines name the area in the words below; both carry Undo
  // (`proposalUndo`). The ask's own line, with the pain rule, is the
  // backend's, in the coach language.
  injuryClear: "It's clear",
  injuryNotYet: 'Not yet',
  injuryClearedRecord: 'Cleared. Your {area} is back in your sessions.',
  injuryNotYetRecord: 'Your {area} stays protected until you clear it.',
  // The six areas an injury names (#269's own), lowercase, for a sentence:
  // one list for the record lines, the Protected group and its confirm
  // (PC-I138). A group heading capitalises the first letter.
  injuryAreaShoulder: 'shoulder',
  injuryAreaElbowWrist: 'elbow or wrist',
  injuryAreaBack: 'back',
  injuryAreaHip: 'hip',
  injuryAreaKnee: 'knee',
  injuryAreaAnkle: 'ankle',
  // For an area this client has no word for: never a guessed one.
  injuryAreaUnknown: 'injured area',

  // ── Protected areas, cleared later (PC-I69, PC-I137) ──
  // The group in Settings (iOS) and on Profile's Coach tab (web), shown only
  // while something is protected. A clear asks first; neither answer is a
  // warning colour, since clearing is not destructive.
  protectedTitle: 'Protected',
  protectedSince: 'since {date}',
  protectedClear: 'Clear',
  protectedConfirmTitle: 'Clear your {area}?',
  protectedConfirmBody:
    'It goes back into your sessions. If it still hurts, stop and see a physio or doctor.',
  protectedConfirmYes: 'Clear',
  protectedConfirmNo: 'Keep protected',

  // ── An exercise added to a template, an offer (PC-I75) ──
  // One shape for every kind that offers to add an exercise next to what a
  // template already holds (the stale routine's, the lagging region's, a
  // plateau's variation): a kind supplies only its decline. The line above,
  // in code's words and the coach language, names the exercise; the pill
  // names the template it goes into.
  addOfferAccept: 'Add to {template}',
  // Only what `add_rows` guarantees (ADR 0005, SB-6): one entry inserted,
  // every other entry keeping its content and its order. Where it lands is
  // in the coach's line, never here.
  addOfferScope: 'Adds one exercise. Everything already there stays as it is.',
  // After a yes, from the answer the server recorded: "already_there" when
  // the template already named the exercise (#268's keep, IS-I39), so the
  // first is never said for a write that did not happen.
  addOfferAdded: 'Added to {template}.',
  addOfferAlreadyThere: '{template} already has it.',
  // The declines, one per kind (PC-I75): each records the decline and folds
  // the line away, with no record line and no Undo after it.
  addOfferKeepRoutine: 'Keep my routine',
  addOfferNotNow: 'Not now',
  addOfferHoldingThis: "I'm happy holding this",

  // ── A plateau's rep range and its deload, adjustments to offer (#272) ──
  // Not add offers: they change today's targets, so an accepted one is an
  // adjustment, computed when the session is built and never written into a
  // template (ADR 0005). The same opener shape as the add offer (an amber
  // pill, "I'm happy holding this" above, a scope line, and after a yes a
  // record line with Undo while the answer can still change, PC-I111). Where
  // the phone shows adjustments only (PC-I112), so "Each set shows why" is
  // true. The wordings are PC-I132's. "Start a lighter week" is right only
  // while the deload is seven days (PC-I110): a backend test fails if the
  // days change while the word stays.
  adjustOfferAcceptRange: 'Try {low} to {high} reps',
  adjustOfferAcceptDeload: 'Start a lighter week',
  adjustOfferScopeRange: 'For {weeks} weeks, this lift only. Your plan stays as it is.',
  adjustOfferScopeDeload:
    'For {days} days, every lift at {share} of its usual weight. Your plan stays as it is.',
  adjustOfferRangeRecord: 'Noted: {low} to {high} reps for {weeks} weeks. Each set shows why.',
  adjustOfferDeloadRecord: 'Noted: a lighter week. Each set shows why.',

  // ── A spent AI budget (#394, C27; owner rulings CAP-O16, CAP-O19 and CAP-O25) ──
  // The coach's own message when a chat turn is refused with a 429 whose
  // `reason` is `ai_budget_spent`, and the strip above the composer while it
  // lasts. Interface chrome, so English whatever the coach speaks: the model
  // is not asked on a spent budget, and the server sends no sentence, only the
  // window and the instant. `{when}` is filled by the client with its own
  // platform time formatter, in the device's zone (the web today; iOS when its build carries
  // the handover, `docs/plans/2026-10-06-394-ios-spent-budget-handover.md`): "today at 2:03 PM",
  // "tomorrow at 2:03 PM", "on Oct 31 at 7:00 PM". Every line says that
  // logging and history keep working (user story 5), which is true because
  // nothing but a model call is refused. No Upgrade control: there is no
  // paywall yet, and a paywall refusal will be a different status and reason.
  budgetSpentDay:
    "I've used up my coaching for now. I'm back {when}. Logging and your history keep working.",
  budgetSpentMonth:
    "I've used up this month's coaching. I'm back {when}. Logging and your history keep working.",
  // A budget of zero never renews: no instant, so no `{when}`. The bubble keeps this
  // sentence; the strip has its own, shorter one below (CAP-O25).
  budgetSpentOff:
    'Coaching is switched off for your account. Logging and your history keep working.',
  budgetStrip: 'Coaching used up. Back {when}.',
  // The strip for a budget of zero: no instant, so no "Back {when}".
  budgetStripOff: 'Coaching is switched off.',
  // What `{when}` is made of (ruling CAP-O33): the words around the time, in the deck
  // so both clients say them the same. The same calendar day as now, the next one, or a later
  // date. `{time}` and `{date}` are each client's own formatter's output in the device's
  // zone, and are NOT deck text: the web writes them in a fixed English 12-hour format
  // ("2:03 PM"), the phone with its own clock and region ("14:03" on a 24-hour phone).
  // Decided for now (each client formats every other date that way); an open point for
  // the owner.
  budgetWhenToday: 'today at {time}',
  budgetWhenTomorrow: 'tomorrow at {time}',
  budgetWhenLater: 'on {date} at {time}',
} as const
